import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Name of the area being guarded, shown in the fallback. */
  area?: string;
}

interface State {
  error: Error | null;
}

/**
 * Stops one broken component taking the whole board down.
 *
 * Without this, a render error anywhere blanks the entire app -- which during a
 * show means the operator loses the mic plot completely and has no way back
 * except a reload they may not think to try. A contained failure that names
 * itself and offers a retry is survivable; a white screen is not.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console rather than sent anywhere: the venue's uplink is the
    // least reliable part of the chain and this is a diagnostic, not telemetry.
    console.error(`[micplot] ${this.props.area ?? 'component'} failed`, error, info);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="err-boundary" role="alert">
        <strong>{this.props.area ?? 'This part of the app'} stopped working.</strong>
        <p>
          The rest of the board is still live. Try again, or switch views and come back.
        </p>
        <pre>{error.message}</pre>
        <div className="row">
          <button className="btn primary" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button className="btn" onClick={() => location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}
